import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Send } from "lucide-react";
import type { UserReview } from "@/lib/earning-apps/reviews";
import {
  listUserAppReviews,
  submitUserAppReview,
  type PublicUserReview,
} from "@/lib/earning-apps/user-reviews.functions";

export function StarRow({ value, className = "w-3.5 h-3.5" }: { value: number; className?: string }) {
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-400" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} viewBox="0 0 20 20" className={className} fill={i <= full ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}>
          <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 15l-5.2 2.8 1-5.9L1.5 7.7l5.9-.9z" />
        </svg>
      ))}
    </span>
  );
}

function ReviewCard({ r }: { r: UserReview }) {
  const initials = r.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-card to-card/40 p-4 hover:border-primary/40 transition-colors shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary text-sm font-bold ring-1 ring-primary/30">
          {initials || "U"}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold truncate">{r.name} <span aria-hidden>{r.country}</span></div>
          <div className="text-xs text-muted-foreground">{[r.city, r.date].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      <div className="mt-2"><StarRow value={r.stars} /></div>
      <p className="mt-2 text-sm text-foreground/90 leading-relaxed whitespace-pre-line">{r.text}</p>
    </div>
  );
}

function toDisplay(r: PublicUserReview): UserReview {
  return {
    name: r.name || "Anonymous",
    city: "Community member",
    country: "🌍",
    stars: Math.min(5, Math.max(1, r.stars)) as UserReview["stars"],
    date: new Date(r.created_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
    text: r.body,
  };
}

export function UserReviewsSection({
  slug,
  appName,
  editorial,
}: {
  slug: string;
  appName: string;
  editorial: UserReview[];
}) {
  const [community, setCommunity] = useState<UserReview[]>([]);
  const [filter, setFilter] = useState<number | null>(null);
  const load = useServerFn(listUserAppReviews);

  useEffect(() => {
    let alive = true;
    load({ data: { slug } })
      .then((res) => { if (alive) setCommunity(res.rows.map(toDisplay)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const all = useMemo(() => [...editorial, ...community], [editorial, community]);
  const avg = all.length ? Math.round((all.reduce((a, b) => a + b.stars, 0) / all.length) * 10) / 10 : 0;
  const counts = useMemo(() => {
    const c: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    all.forEach((r) => { c[r.stars] = (c[r.stars] ?? 0) + 1; });
    return c;
  }, [all]);

  const shown = filter ? all.filter((r) => r.stars === filter) : all;

  return (
    <section className="mx-auto max-w-3xl px-4 mt-10">
      <h2 className="text-xl md:text-2xl font-bold mb-3">Real user reviews of {appName}</h2>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <StarRow value={avg} />
        <span className="font-semibold text-foreground">{avg.toFixed(1)} / 5</span>
        <span>· {all.length} reviews</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => setFilter(null)}
          className={`rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition-colors ${filter === null ? "bg-primary text-primary-foreground ring-primary" : "bg-muted/50 ring-border hover:bg-muted"}`}
        >
          All ({all.length})
        </button>
        {[5, 4, 3, 2, 1].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            aria-pressed={filter === s}
            className={`rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition-colors ${filter === s ? "bg-primary text-primary-foreground ring-primary" : "bg-muted/50 ring-border hover:bg-muted"}`}
          >
            {s} ★ ({counts[s] ?? 0})
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No {filter} star reviews yet for {appName}. Be the first to write one below.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {shown.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
      )}

      <ReviewForm
        slug={slug}
        appName={appName}
        onPosted={(r) => setCommunity((prev) => [toDisplay(r), ...prev])}
      />

      <p className="mt-3 text-xs text-muted-foreground">
        Reviews collected from DrHint community feedback, Trustpilot, r/beermoney and Play Store threads,
        plus reviews submitted by readers on this page. Names shortened for privacy.
      </p>
    </section>
  );
}

function ReviewForm({
  slug,
  appName,
  onPosted,
}: {
  slug: string;
  appName: string;
  onPosted: (r: PublicUserReview) => void;
}) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const submit = useServerFn(submitUserAppReview);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stars) { setMsg("Please choose a star rating first."); return; }
    if (body.trim().length < 5) { setMsg("Please write at least a few words."); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await submit({ data: { slug, name: name.trim() || null, stars, body: body.trim(), honeypot } });
      if (!res.ok) {
        setMsg(res.error === "rate_limited" ? "Too many reviews from your device. Try again later." : "Could not post your review.");
        return;
      }
      onPosted({
        id: res.id ?? String(Date.now()),
        name: name.trim() || "Anonymous",
        stars,
        body: body.trim(),
        created_at: new Date().toISOString(),
      });
      setBody(""); setName(""); setStars(0);
      setMsg("Thanks! Your review is live.");
    } catch {
      setMsg("Network error. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 rounded-2xl border border-border bg-card/50 p-4">
      <h3 className="text-base font-bold">Write your own review of {appName}</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Step 1 — choose your star rating. Step 2 — share your real payout experience.
      </p>

      <div className="mt-3 flex items-center gap-1" role="radiogroup" aria-label="Your star rating">
        {[1, 2, 3, 4, 5].map((i) => (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={stars === i}
            aria-label={`${i} star${i > 1 ? "s" : ""}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setStars(i)}
            className="p-1 text-amber-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <svg viewBox="0 0 20 20" className="w-7 h-7" fill={i <= (hover || stars) ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}>
              <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 15l-5.2 2.8 1-5.9L1.5 7.7l5.9-.9z" />
            </svg>
          </button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">{stars ? `${stars} / 5` : "Select stars"}</span>
      </div>

      <input
        type="text"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <input
        type="text"
        value={name}
        maxLength={40}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        aria-label="Your name"
        className="mt-3 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
      />
      <textarea
        value={body}
        maxLength={1200}
        rows={4}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Did ${appName} pay you? How long did the payout take? Anything other users should know?`}
        aria-label="Your review"
        className="mt-2 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm"
        required
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">{body.length}/1200 · Be honest. No abuse, spam or referral links.</span>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post review
        </button>
      </div>
      {msg && <p className="mt-2 text-xs text-muted-foreground">{msg}</p>}
    </form>
  );
}
